import { SkeletonBodyText, SkeletonDisplayText } from "@shopify/polaris";

import { useAdminLoading } from "../utils/adminLoading";

/**
 * One loading picture for a route, drawn at BOTH of its loading gates.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PROBLEM THIS SOLVES: routes were showing TWO DIFFERENT SPINNERS per wait.
 * ─────────────────────────────────────────────────────────────────────────────
 * Every in-app navigation has two serial gates, and they were drawing different
 * pictures:
 *
 *   1. `<Suspense fallback={<RouteLoader/>}>` in components/App.jsx — a LARGE
 *      `s-spinner size="large-100"` centred in `minHeight: 60vh` — while the
 *      route's JS chunk downloads.
 *   2. The route's own `if (fetching)` branch — typically a SMALLER, unsized
 *      spinner under an `<s-page heading>` — while its first query runs.
 *   3. The real page.
 *
 * So the merchant saw a big centred spinner, then a small spinner somewhere else
 * with a heading that had just appeared, then content: three pictures and two
 * layout jumps for one wait. Reported on /billing ("one big and one small") and
 * present identically on /integrations.
 *
 * Rendering THIS at both gates collapses it to: skeleton → skeleton → content —
 * a single unmoving frame. Same principle docs/architecture/admin-load-time.md
 * applied to the dashboard boot (blank → spinner → skeleton became one shell);
 * this is the in-app-navigation equivalent, which that work did not cover.
 *
 * ⚠️ THE `heading` MUST MATCH THE ROUTE'S OWN `<s-page heading>` EXACTLY.
 * The heading is the anchor that makes the handoff invisible: the skeleton paints
 * it, then the real page repaints it identically, so nothing moves. If the two
 * drift apart the heading visibly changes at handoff and this component stops
 * doing the only job it has. There is no test enforcing this — keep them in step
 * by hand, and note that `verify-boot-cover.mjs` covers the BOOT shell only, not
 * these route-level ones.
 *
 * ⚠️ Both gates must render the SAME element. A route that uses this for its
 * `fetching` branch but leaves `RouteLoader` as its Suspense fallback (or vice
 * versa) reintroduces exactly the mismatch above.
 *
 * `sections` is a rough shape, not a pixel match — enough to reserve vertical
 * space so the real content landing does not shove the viewport.
 *
 * NOTE ON PRIMITIVES: SkeletonDisplayText / SkeletonBodyText are Polaris REACT
 * holdouts, used for the same reason TemplateGrid.jsx does — there is no s-*
 * skeleton web component. Both survive the CSS purge in
 * vite-plugins/ns-polaris-css-purge.mjs (which strips only IndexTable,
 * Navigation, Frame, TopBar and nine other unrendered roots). Do not add
 * Skeleton* to that allowlist.
 *
 * ⚠️ `inlineSize` MUST ALSO MATCH. It is the page's width, so a route whose real
 * `<s-page>` carries `inlineSize="large"` (/analytics, /templates) and whose
 * skeleton omits it draws a NARROWER page that snaps wider at handoff — the exact
 * jump this component exists to remove, just on the horizontal axis. Omitted here
 * by default because `s-page`'s own default is the narrow one, so the two agree
 * for every route that does not set it.
 *
 * @param {object}   props
 * @param {string}   props.heading  Must equal the route's own `<s-page heading>`.
 * @param {number[]} props.sections One entry per section; the number is how many
 *                                  body-text lines to draw under that section's
 *                                  title. Defaults to a single 3-line section.
 * @param {string}   [props.inlineSize] Must equal the route's own `<s-page inlineSize>`.
 * @param {React.ReactNode} [props.children] Extra skeleton shape drawn AFTER the
 *                                  sections — for pages whose body is not a plain
 *                                  section stack (e.g. the template grid).
 */
export default function PageSkeleton({ heading, sections = [3], inlineSize, children }) {
  // Also drive Shopify's own header progress bar, outside our iframe — the same
  // affordance the admin and first-party apps (Flow) use. See utils/adminLoading.js.
  useAdminLoading();

  return (
    <s-page heading={heading} inlineSize={inlineSize}>
      <s-stack direction="block" gap="base">
        {sections.map((lines, i) => (
          <s-section key={i}>
            <s-stack direction="block" gap="base">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={lines} />
            </s-stack>
          </s-section>
        ))}
        {children}
      </s-stack>
    </s-page>
  );
}
